import { BuildingDamages, ContentDamages, GuaranteeTC, ObjectDamages, PolicySubTypeTC, RimItemTC, RimSectionTC, VatPercentageTC, VatThresholdTC } from '../models/typecode.enums';
import { BusinessPolicy, BusinessResult, BusinessResultType, BusinessValidation, BusinessValidationType } from '../models/policy-business.model';
import { getIBANLength, isValidBBAN, isValidIBAN } from './bank-account-validation';

import { BusinessRimLine, createBusinessRimLine } from '../models/rim-business.model';
import { Constants } from '../models/constants';
import { TypeCodeTranslation } from '../models/shared.model';
import { isValidEmail } from './shared-validation';
import { RimLine } from '../models/types';

export function businessRimSectionChange(policy: BusinessPolicy): BusinessResult {
  const validationInProgress: BusinessValidation = {
    businessValidationType: BusinessValidationType.none,
    errorMessage: '',
    hasError: false,
  };
  const result: BusinessResult = {
    resultType: BusinessResultType.ok,
    messages: [],
    data: null,
    validations: [validationInProgress],
  };

  const caseCode = policy.id;
  const rimSectionTC = policy.Rim.currentRimLine.rimSectionTC;
  const rimItems: TypeCodeTranslation[] = policy.Rim.rimItemValues;
  const rimLinesItemTC = policy.Rim.rimLines.filter(x => x.rimSectionTC === rimSectionTC && x.deleted !== true).map(x => {
    return x.rimItemTC;
  });
  const rimItemsSelected = rimItems.filter((x) => x.parentCode === rimSectionTC).map(item => ({...item}));
  rimItemsSelected.forEach(item => {
    item.enabled = true;
    if (rimLinesItemTC.includes(item.typeCodeValue)) {
      item.enabled = false;
    } else {
      switch (item.typeCodeValue) {
        case RimItemTC.Vrijstelling:
        case RimItemTC.VrijstellingInhoudKelder:
          item.enabled = !(rimLinesItemTC.includes(RimItemTC.Vrijstelling) || rimLinesItemTC.includes(RimItemTC.VrijstellingInhoudKelder));
          break;
        case RimItemTC.TerugvorderbareVrijstelling:
          item.enabled = !rimLinesItemTC.includes(RimItemTC.CompOptieFranchise)
            && !rimLinesItemTC.includes(RimItemTC.CompOscars)
            && !rimLinesItemTC.includes(RimItemTC.TerugvorderbareVrijstelling);
          break;
        case RimItemTC.CompOptieFranchise:
          item.enabled = !rimLinesItemTC.includes(RimItemTC.CompOptieFranchise)
            && !rimLinesItemTC.includes(RimItemTC.CompOscars)
            && !rimLinesItemTC.includes(RimItemTC.TerugvorderbareVrijstelling)
            && (policy.policySubTypeTC !== PolicySubTypeTC.BelfiusHome || caseCode > 10000000)
            && (caseCode > 10000000 && policy.loweredExcessAmountThreshold > 0); // If lowered excessAmountThreshold is bigger then 0 then it is an english franchise
          break;
        case RimItemTC.CompOscars:
          item.enabled = !rimLinesItemTC.includes(RimItemTC.CompOptieFranchise)
            && !rimLinesItemTC.includes(RimItemTC.CompOscars)
            && !rimLinesItemTC.includes(RimItemTC.TerugvorderbareVrijstelling)
            && (policy.policySubTypeTC !== PolicySubTypeTC.BelfiusHome || caseCode > 10000000);
          break;
        case RimItemTC.OV:
          item.enabled = !(rimLinesItemTC.includes(RimItemTC.OV));
          break;
        default:
          break;
      }
    }
  });

  policy.Rim.currentRimLine.rimItemTC = '';
  policy.Rim.currentRimLine.rimItemValuesBySection = rimItemsSelected;
  result.data = policy;
  return result;
}

export function businessRimItemChange(policy: BusinessPolicy): BusinessResult {
  const validationInProgress: BusinessValidation = {
    businessValidationType: BusinessValidationType.none,
    errorMessage: '',
    hasError: false,
  };
  const result: BusinessResult = {
    resultType: BusinessResultType.ok,
    messages: [],
    data: null,
    validations: [validationInProgress],
  };

  policy.Rim.currentRimLine.amount = getRimLineAmount(policy);
  policy.Rim.currentRimLine.vatEnabled = getRimLineVatEnabled(policy.Rim.currentRimLine.rimItemTC);
  policy.Rim.currentRimLine.amountEnabled = getRimLineAmountEnabled(policy.Rim.currentRimLine.rimItemTC);
  policy.Rim.currentRimLine.vatPercentageTC = getRimVatPercentageTCDefault(policy);

  result.data = policy;

  return result;
}

function getRimLineAmount(policy: BusinessPolicy): string {
  switch (policy.Rim.currentRimLine.rimItemTC) {
    case RimItemTC.Vrijstelling:
      return (-policy.excessAmount).toString();
    case RimItemTC.TerugvorderbareVrijstelling:
    case RimItemTC.CompOptieFranchise:
    case RimItemTC.CompOscars: {
      const result = policy.Rim.rimLines.find(x => x.rimItemTC === RimItemTC.Vrijstelling && x.deleted !== true);
      if (result) {
        return (-result.amount).toString();
      }
      return '0';
    }
    case RimItemTC.VrijstellingInhoudKelder:
      return (-policy.excessAmount).toString();
    case RimItemTC.OV:
      return calculateOV(policy);
    default:
      return '';
  }
}

function getRimLineVatEnabled(rimItemTC: string): boolean {
  switch (rimItemTC) {
    case RimItemTC.CompOptieFranchise:
    case RimItemTC.CompOscars:
    case RimItemTC.Evenredigheid:
    case RimItemTC.OV:
    case RimItemTC.TerugvorderbareVrijstelling:
    case RimItemTC.TO:
    case RimItemTC.Voorschot:
    case RimItemTC.Vrijstelling:
    case RimItemTC.VrijstellingInhoudKelder:
      return false;
    default:
      return true;
  }
}

function getRimLineAmountEnabled(rimItemTC: string): boolean {
  switch (rimItemTC) {
    case RimItemTC.OV:
    case RimItemTC.TerugvorderbareVrijstelling:
    case RimItemTC.VrijstellingInhoudKelder:
      return false;
    default:
      return true;
  }
}

function getRimVatPercentageTCDefault(policy: BusinessPolicy): VatPercentageTC | '' {
  const rimSectionTC = policy.Rim.currentRimLine.rimSectionTC as RimSectionTC;
  const vatThresholdTC = policy.Rim.currentRimLine.vatThresholdTC as VatThresholdTC;
  if (!vatThresholdTC || vatThresholdTC === VatThresholdTC.For) {
    return '';
  }
  if (BuildingDamages.includes(rimSectionTC) || ObjectDamages.includes(rimSectionTC)) {
    return VatPercentageTC.Vat6;
  } else if (ContentDamages.includes(rimSectionTC)) {
    return VatPercentageTC.Vat21;
  } else {
    return '';
  }
}

export function businessRimVatThresholdChange(policy: BusinessPolicy): BusinessResult {
  const validationInProgress: BusinessValidation = {
    businessValidationType: BusinessValidationType.none,
    errorMessage: '',
    hasError: false,
  };
  const result: BusinessResult = {
    resultType: BusinessResultType.ok,
    messages: [],
    data: null,
    validations: [validationInProgress],
  };
  const vatThresholdTC = policy.Rim.currentRimLine.vatThresholdTC;
  policy.Rim.currentRimLine.vatPercentageEnabled = !(vatThresholdTC === VatThresholdTC.For);
  policy.Rim.currentRimLine.vatPercentageTC = getRimVatPercentageTCDefault(policy);

  if (BuildingDamages.includes(policy.Rim.currentRimLine.rimSectionTC as RimSectionTC)
    && isCocoon(policy.conditions)
    && policy.Rim.currentRimLine.rimItemTC
    && (policy.Rim.currentRimLine.vatPercentageTC === VatPercentageTC.Vat6 || policy.Rim.currentRimLine.vatPercentageTC === VatPercentageTC.Vat21)
    && (policy.Rim.currentRimLine.vatThresholdTC === VatThresholdTC.Exc || policy.Rim.currentRimLine.vatThresholdTC === VatThresholdTC.Inc)) {
    policy.Rim.currentRimLine.invoicePresentEnabled = true;
    if (policy.Rim.currentRimLine.vatThresholdTC === VatThresholdTC.Exc) {
      policy.Rim.currentRimLine.invoicePresent = true;
    } else {
      policy.Rim.currentRimLine.invoicePresent = false;
    }
  } else {
    policy.Rim.currentRimLine.invoicePresentEnabled = false;
    policy.Rim.currentRimLine.invoicePresent = false;
  }

  result.data = policy;
  return result;
}

export function businessAddRimLine(policy: BusinessPolicy): BusinessResult {
  const validationInProgress: BusinessValidation = {
    businessValidationType: BusinessValidationType.none,
    errorMessage: '',
    hasError: false,
  };
  const result: BusinessResult = {
    resultType: BusinessResultType.ok,
    messages: [],
    data: null,
    validations: [validationInProgress],
  };
  const rimLine: BusinessRimLine = policy.Rim.currentRimLine;
  const rimLines: BusinessRimLine[] = policy.Rim.rimLines.filter(x => x.deleted !== true);
  const rimLinesWithoutGeneral: BusinessRimLine[] = rimLines.filter(x => x.rimSectionTC !== RimSectionTC.Gen);

  if (rimLine.rimSectionTC !== RimSectionTC.Gen
    && rimLinesWithoutGeneral.length > 0
    && !(ObjectDamages.includes(rimLine.rimSectionTC as RimSectionTC) === ObjectDamages.includes(rimLinesWithoutGeneral[0].rimSectionTC as RimSectionTC))) {
    result.resultType = BusinessResultType.error;
    result.messages = ['details.rim.error.mixedObjectWithBuildingOrContent'];
  }

  switch (rimLine.rimItemTC) {
    case RimItemTC.OV:
    case RimItemTC.TerugvorderbareVrijstelling:
    case RimItemTC.VrijstellingInhoudKelder:
      if (!(+rimLine.amount > 0) || +rimLine.amount !== +getRimLineAmount(policy)) {
        result.resultType = BusinessResultType.error;
        result.messages = ['details.rim.error.rimLineAmount'];
      }
      break;
    case RimItemTC.AAndO:
    case RimItemTC.AnderePlus:
    case RimItemTC.Hoofdsom:
    case RimItemTC.Materiaal:
    case RimItemTC.RAndB:
    case RimItemTC.Werkuren:
      if (!(+rimLine.amount > 0 && rimLine.vatThresholdTC !== '' && (rimLine.vatThresholdTC === VatThresholdTC.For || rimLine.vatPercentageTC !== ''))) {
        result.resultType = BusinessResultType.error;
        result.messages = ['details.rim.error.rimLineAmount'];
      }
      break;
    case RimItemTC.Voorschot:
      if (+rimLine.amount === 0) {
        result.resultType = BusinessResultType.error;
        result.messages = ['details.rim.error.rimLineAmount'];
      }
      break;
    case RimItemTC.Vrijstelling:
      if (+rimLine.amount >= 0) {
        result.resultType = BusinessResultType.error;
        result.messages = ['details.rim.error.rimLineAmount'];
      }
      break;
    default:
      if (+rimLine.amount <= 0) {
        result.resultType = BusinessResultType.error;
        result.messages = ['details.rim.error.rimLineAmount'];
      }
      break;
  }

  if (result.resultType === BusinessResultType.ok) {
    policy.Rim.rimLines = addRimLine(policy);
    let vat6ToBeJustifiedAmount = 0;
    let vat21ToBeJustifiedAmount = 0;
    policy.Rim.rimLines.filter(x => x.deleted !== true).forEach(x => {
      vat6ToBeJustifiedAmount = x.vatThresholdTC === VatThresholdTC.Exc && x.vatPercentageTC === VatPercentageTC.Vat6 ? vat6ToBeJustifiedAmount + +x.amount : vat6ToBeJustifiedAmount;
      vat21ToBeJustifiedAmount = x.vatThresholdTC === VatThresholdTC.Exc && x.vatPercentageTC === VatPercentageTC.Vat21 ? vat21ToBeJustifiedAmount + +x.amount : vat21ToBeJustifiedAmount;
    });
    policy.Rim.vat6ToBeJustifiedAmount = vat6ToBeJustifiedAmount;
    policy.Rim.vat21ToBeJustifiedAmount = vat21ToBeJustifiedAmount;

    policy.Rim.currentRimLine = createBusinessRimLine();
    const rimLineOV = rimLines.find(x => x.rimItemTC === RimItemTC.OV);
    if (rimLineOV) {
      rimLineOV.amount = calculateOV(policy);
      rimLineOV.amount80 = calculateOV80(policy);
    }
  }

  result.data = policy;
  return result;
}

function addRimLine(policy: BusinessPolicy): BusinessRimLine[] {
  const rimLines: BusinessRimLine[] = policy.Rim.rimLines;
  const item: BusinessRimLine = policy.Rim.currentRimLine;
  item.sequence = Math.max(...rimLines.map(x => x.sequence), 0) + 1;
  if (item.vatThresholdTC !== '') {
    const selectedRimSectionItems = rimLines.filter(x => x.rimSectionTC === item.rimSectionTC && x.rimSectionTC !== RimSectionTC.Gen && x.deleted !== true);
    selectedRimSectionItems.forEach(x => {
      if (x.vatThresholdTC) {
        x.vatThresholdTC = item.vatThresholdTC;
        x.vatPercentageTC = item.vatPercentageTC;
      }
    });
  }
  if (item.rimItemTC === RimItemTC.Voorschot || item.rimItemTC === RimItemTC.Evenredigheid) {
    item.amount = (-Math.abs(+item.amount)).toString();
  }

  if (item.invoicePresent) {
    item.amount80 = (+item.amount * 0.8).toString();
  } else if (item.rimItemTC === RimItemTC.OV && isCocoon(policy.conditions)) {
    item.amount80 = calculateOV80(policy);
  } else if (isCocoon(policy.conditions)) {
    item.amount80 = item.amount;
  } else {
    item.amount80 = '0';
  }

  return [...rimLines, item];
}

export function getRimLineAmount100(vatThresholdTC: string, amount: string, vatPercentageTC: string): number {
  if (vatThresholdTC === VatThresholdTC.Exc) {
    return (+amount * (100 + +vatPercentageTC / 100) / 100);
  } else {
    return +amount;
  }
}

function calculateOV(policy: BusinessPolicy): string {
  let result = '0';
  const rimLines = policy.Rim.rimLines;
  const caseCode = policy.id;

  let total = 0;
  if (caseCode > 10000000) {
    // These are the Axon cases
    // We need to determine if there is an English Franchise applicable
    if (policy.loweredExcessAmountThreshold > 0) { // If bigger then 0 then there is an English Franchise

      // In case of an English Franchise the user has to select the excess amount and the comp.opt.franchise
      // So, we should not ignore the excess and comp.opt.fran because it will not be sumated else
      rimLines.filter(x => x.deleted !== true).forEach(item => {
        if (item.rimItemTC &&
          item.rimItemTC !== RimItemTC.OV &&
          item.rimItemTC !== RimItemTC.TerugvorderbareVrijstelling &&
          item.rimItemTC !== RimItemTC.CompOscars &&
          item.rimItemTC !== RimItemTC.Voorschot) {
          
          // Business logic for adding item amounts based on guarantee and policy subtype
          // Check if the guarantee is NOT "DIBDiOp" (Theft or AxonTheft)
          if (
            policy.guaranteeTCContent !== GuaranteeTC.Theft &&
            policy.guaranteeTCContent !== GuaranteeTC.AxonTheft
          ) {
            // If the policy subtype is NOT Dexia Brandpolis (PVDIB001)
            if (policy.policySubTypeTC !== PolicySubTypeTC.BelfiusHome) {
              // Exclude "A&O" and "R&B" RimItems for Dexia Brandpolis
              if (
                item.rimItemTC !== RimItemTC.AAndO &&
                item.rimItemTC !== RimItemTC.RAndB
              ) {
                total += +item.amount;
              }
            } else {
              // For all other policy subtypes, add all RimItems
              total += +item.amount;
            }
          } else {
            // If the guarantee IS "DIBDiOp" (Theft or AxonTheft)
            // Only add RimItems from RimSections "Building" or "General"
            if (
              item.rimSectionTC === RimSectionTC.Gen ||
              BuildingDamages.includes(item.rimSectionTC as RimSectionTC)
            ) {
              total += +item.amount;
            }
          }
        }
      });
    } else {
      rimLines.filter(x => x.deleted !== true).forEach(item => {
        if (item.rimItemTC &&
          item.rimItemTC !== RimItemTC.OV &&
          item.rimItemTC !== RimItemTC.TerugvorderbareVrijstelling &&
          item.rimItemTC !== RimItemTC.CompOscars &&
          item.rimItemTC !== RimItemTC.CompOptieFranchise &&
          item.rimItemTC !== RimItemTC.Voorschot) {
          
          // Business logic for adding item amounts based on guarantee and policy subtype
          // Check if the guarantee is NOT "DIBDiOp" (Theft or AxonTheft)
          if (
            policy.guaranteeTCContent !== GuaranteeTC.Theft &&
            policy.guaranteeTCContent !== GuaranteeTC.AxonTheft
          ) {
            // If the policy subtype is NOT Dexia Brandpolis (PVDIB001)
            if (policy.policySubTypeTC !== PolicySubTypeTC.BelfiusHome) {
              // Exclude "A&O" and "R&B" RimItems for Dexia Brandpolis
              if (
                item.rimItemTC !== RimItemTC.AAndO &&
                item.rimItemTC !== RimItemTC.RAndB
              ) {
                total += +item.amount;
              }
            } else {
              // For all other policy subtypes, add all RimItems
              total += +item.amount;
            }
          } else {
            // If the guarantee IS "DIBDiOp" (Theft or AxonTheft)
            // Only add RimItems from RimSections "Building" or "General"
            if (
              item.rimSectionTC === RimSectionTC.Gen ||
              BuildingDamages.includes(item.rimSectionTC as RimSectionTC)
            ) {
              total += +item.amount;
            }
          }
        }
      });
    }
  } else {
    // These are the claint cases
    rimLines.filter(x => x.deleted !== true).forEach(item => {
      if (item.rimItemTC &&
        item.rimItemTC !== RimItemTC.OV &&
        item.rimItemTC !== RimItemTC.TerugvorderbareVrijstelling &&
        item.rimItemTC !== RimItemTC.CompOscars &&
        item.rimItemTC !== RimItemTC.CompOptieFranchise &&
        item.rimItemTC !== RimItemTC.Voorschot) {
        // Business logic for adding item amounts based on guarantee and policy subtype
          // Check if the guarantee is NOT "DIBDiOp" (Theft or AxonTheft)
          if (
            policy.guaranteeTCContent !== GuaranteeTC.Theft &&
            policy.guaranteeTCContent !== GuaranteeTC.AxonTheft
          ) {
            // If the policy subtype is NOT Dexia Brandpolis (PVDIB001)
            if (policy.policySubTypeTC !== PolicySubTypeTC.BelfiusHome) {
              // Exclude "A&O" and "R&B" RimItems for Dexia Brandpolis
              if (
                item.rimItemTC !== RimItemTC.AAndO &&
                item.rimItemTC !== RimItemTC.RAndB
              ) {
                total += +item.amount;
              }
            } else {
              // For all other policy subtypes, add all RimItems
              total += +item.amount;
            }
          } else {
            // If the guarantee IS "DIBDiOp" (Theft or AxonTheft)
            // Only add RimItems from RimSections "Building" or "General"
            if (
              item.rimSectionTC === RimSectionTC.Gen ||
              BuildingDamages.includes(item.rimSectionTC as RimSectionTC)
            ) {
              total += +item.amount;
            }
          }
      }
    });
  }

  if ((policy.indirectLossesBuilding || policy.indirectLossesContent) && total > 0) {
    result = Number(Math.round(Number(total * policy.indirectLossesPercentage / 100 + 'e2')) + 'e-2').toString();
  }

  return result;
}

function calculateOV80(policy: BusinessPolicy): string {
  let result = '0';
  const rimLines = policy.Rim.rimLines;
  const caseCode = policy.id;

  let total = 0;
  if (caseCode > 10000000) {
    // These are the Axon cases
    // We need to determine if there is an English Franchise applicable
    if (policy.loweredExcessAmountThreshold > 0) { // If bigger then 0 then there is an English Franchise
      // Here we ignore the CompOptieFranchise for the OV calculation
      rimLines.filter(x => x.deleted !== true).forEach(item => {
        if (item.rimItemTC &&
          item.rimItemTC !== RimItemTC.OV &&
          item.rimItemTC !== RimItemTC.TerugvorderbareVrijstelling &&
          item.rimItemTC !== RimItemTC.CompOscars &&
          item.rimItemTC !== RimItemTC.Voorschot &&
          item.rimItemTC !== RimItemTC.CompOptieFranchise &&
          item.rimItemTC !== RimItemTC.Vrijstelling) {
          if ((item.rimItemTC === RimItemTC.Vrijstelling && +item.amount === policy.excessAmount)) {
            if (policy.raisedExcessAmount) {
              total -= policy.raisedExcessAmount;
            } else {
              total -= policy.excessAmount;
            }
          } 
          else 
          {
            // Business logic for adding item amounts based on guarantee and policy subtype
            // Check if the guarantee is NOT "DIBDiOp" (Theft or AxonTheft)
            if (
              policy.guaranteeTCContent !== GuaranteeTC.Theft &&
              policy.guaranteeTCContent !== GuaranteeTC.AxonTheft
            ) {
              // If the policy subtype is NOT Dexia Brandpolis (PVDIB001)
              if (policy.policySubTypeTC !== PolicySubTypeTC.BelfiusHome) {
                // Exclude "A&O" and "R&B" RimItems for Dexia Brandpolis
                if (
                  item.rimItemTC !== RimItemTC.AAndO &&
                  item.rimItemTC !== RimItemTC.RAndB
                ) {
                  total += +item.amount;
                }
              } else {
                // For all other policy subtypes, add all RimItems
                total += +item.amount;
              }
            } else {
              // If the guarantee IS "DIBDiOp" (Theft or AxonTheft)
              // Only add RimItems from RimSections "Building" or "General"
              if (
                item.rimSectionTC === RimSectionTC.Gen ||
                BuildingDamages.includes(item.rimSectionTC as RimSectionTC)
              ) {
                total += +item.amount;
              }
            } 
          }
        }
      });
    } else {
      rimLines.filter(x => x.deleted !== true).forEach(item => {
        if (item.rimItemTC &&
          item.rimItemTC !== RimItemTC.OV &&
          item.rimItemTC !== RimItemTC.TerugvorderbareVrijstelling &&
          item.rimItemTC !== RimItemTC.CompOscars &&
          item.rimItemTC !== RimItemTC.CompOptieFranchise &&
          item.rimItemTC !== RimItemTC.Voorschot) {
          if ((item.rimItemTC === RimItemTC.Vrijstelling && +item.amount === policy.excessAmount)) {
            if (policy.raisedExcessAmount) {
              total -= policy.raisedExcessAmount;
            } else {
              total -= policy.excessAmount;
            }
          } 
          else 
          {
            // Business logic for adding item amounts based on guarantee and policy subtype
            // Check if the guarantee is NOT "DIBDiOp" (Theft or AxonTheft)
            if (
              policy.guaranteeTCContent !== GuaranteeTC.Theft &&
              policy.guaranteeTCContent !== GuaranteeTC.AxonTheft
            ) {
              // If the policy subtype is NOT Dexia Brandpolis (PVDIB001)
              if (policy.policySubTypeTC !== PolicySubTypeTC.BelfiusHome) {
                // Exclude "A&O" and "R&B" RimItems for Dexia Brandpolis
                if (
                  item.rimItemTC !== RimItemTC.AAndO &&
                  item.rimItemTC !== RimItemTC.RAndB
                ) {
                  total += +item.amount;
                }
              } else {
                // For all other policy subtypes, add all RimItems
                total += +item.amount;
              }
            } else {
              // If the guarantee IS "DIBDiOp" (Theft or AxonTheft)
              // Only add RimItems from RimSections "Building" or "General"
              if (
                item.rimSectionTC === RimSectionTC.Gen ||
                BuildingDamages.includes(item.rimSectionTC as RimSectionTC)
              ) {
                total += +item.amount;
              }
            } 
          }
        }
      });
    }
  } else {
    rimLines.filter(x => x.deleted !== true).forEach(item => {
      if (item.rimItemTC &&
        item.rimItemTC !== RimItemTC.OV &&
        item.rimItemTC !== RimItemTC.TerugvorderbareVrijstelling &&
        item.rimItemTC !== RimItemTC.CompOscars &&
        item.rimItemTC !== RimItemTC.CompOptieFranchise &&
        item.rimItemTC !== RimItemTC.Voorschot) {
        if ((item.rimItemTC === RimItemTC.Vrijstelling && +item.amount === policy.excessAmount)) {
          if (policy.raisedExcessAmount) {
            total -= policy.raisedExcessAmount;
          } else {
            total -= policy.excessAmount;
          }
        } 
          else 
          {
            // Business logic for adding item amounts based on guarantee and policy subtype
            // Check if the guarantee is NOT "DIBDiOp" (Theft or AxonTheft)
            if (
              policy.guaranteeTCContent !== GuaranteeTC.Theft &&
              policy.guaranteeTCContent !== GuaranteeTC.AxonTheft
            ) {
              // If the policy subtype is NOT Dexia Brandpolis (PVDIB001)
              if (policy.policySubTypeTC !== PolicySubTypeTC.BelfiusHome) {
                // Exclude "A&O" and "R&B" RimItems for Dexia Brandpolis
                if (
                  item.rimItemTC !== RimItemTC.AAndO &&
                  item.rimItemTC !== RimItemTC.RAndB
                ) {
                  total += +item.amount;
                }
              } else {
                // For all other policy subtypes, add all RimItems
                total += +item.amount;
              }
            } else {
              // If the guarantee IS "DIBDiOp" (Theft or AxonTheft)
              // Only add RimItems from RimSections "Building" or "General"
              if (
                item.rimSectionTC === RimSectionTC.Gen ||
                BuildingDamages.includes(item.rimSectionTC as RimSectionTC)
              ) {
                total += +item.amount;
              }
            } 
          }
      }
    });
  }

  if ((policy.indirectLossesBuilding || policy.indirectLossesContent) && isCocoon(policy.conditions) && total > 0) {
    result = Number(Math.round(Number(total * policy.indirectLossesPercentage / 100 + 'e2')) + 'e-2').toString();
  }

  return result;
}

export function businessRemoveRimLine(policy: BusinessPolicy, sequence: number): BusinessResult {
  const validationInProgress: BusinessValidation = {
    businessValidationType: BusinessValidationType.none,
    errorMessage: '',
    hasError: false,
  };
  const result: BusinessResult = {
    resultType: BusinessResultType.ok,
    messages: [],
    data: null,
    validations: [validationInProgress],
  };
  const rimLine = policy.Rim.rimLines.find(x => x.sequence === sequence);
  if (rimLine) {
    if (rimLine.id === 0) {
      policy.Rim.rimLines.splice(policy.Rim.rimLines.indexOf(rimLine), 1);
    } else {
      rimLine.deleted = true;
    }
  }
  const ovLine = policy.Rim.rimLines.find(x => x.rimItemTC === RimItemTC.OV);
  if (ovLine) {
    ovLine.amount = calculateOV(policy);
    ovLine.amount80 = calculateOV80(policy);
  }
  result.data = policy;
  return result;
}

export function getRimLinesSum(rimLines: RimLine[]): number {
  const result = rimLines.filter(x => x.deleted !== true).reduce((a, b) => a + (+b.amount80 || +b.amount), 0);
  return (result > 0 ? result : 0);
}

export function getRimLinesSum100(rimLines: RimLine[]): number {
  const result = rimLines.filter(x => x.deleted !== true).reduce((a, b) => a + (getRimLineAmount100(b.vatThresholdTC, b.amount, b.vatPercentageTC)), 0);
  return (result > 0 ? result : 0);
}

export function rimValidateForSend(policy: BusinessPolicy): BusinessResult {
  const result = validateRim(policy);

  if (policy.Rim.rimStatusTC === '' || policy.Rim.rimStatusTC === '1') {
    result.resultType = BusinessResultType.error;
    result.messages.push('validation.rim.rimStatusRequired');
  }

  return result;
}

export function validateRim(policy: BusinessPolicy): BusinessResult {
  const validationInProgress: BusinessValidation = {
    businessValidationType: BusinessValidationType.none,
    errorMessage: '',
    hasError: false,
  };
  const result: BusinessResult = {
    resultType: BusinessResultType.ok,
    messages: [],
    data: null,
    validations: [validationInProgress],
  };
  result.data = policy;

  if (!policy.Rim.damageCauseRemark || policy.Rim.damageCauseRemark.trim().length === 0) {
    result.resultType = BusinessResultType.error;
    result.messages.push('validation.rim.damageCauseRemarkRequired');
  }

  const rimLinesValidation = validateRimLines(policy);
  if (rimLinesValidation.resultType === BusinessResultType.error) {
    result.resultType = BusinessResultType.error;
    result.messages.push(...rimLinesValidation.messages);
  }

  if (policy.Rim.paidByBankTransfer) {
    if (!policy.Rim.iban && !policy.Rim.bban) {
      result.resultType = BusinessResultType.error;
      result.messages.push('validation.rim.ibanAccountRequired');
    }
    if (policy.Rim.iban) {
      const ibanValidation = isValidIBAN(policy.Rim.iban);
      if (ibanValidation.resultType === BusinessResultType.error) {
        result.resultType = BusinessResultType.error;
        result.messages.push(...ibanValidation.messages);
      }
    }
    if (policy.Rim.bban) {
      const bbanValidation = isValidBBAN(policy.Rim.bban);
      if (bbanValidation.resultType === BusinessResultType.error) {
        result.resultType = BusinessResultType.error;
        result.messages.push(...bbanValidation.messages);
      }
    }
  }

  if (policy.Rim.sendEmailToPolicyHolder) {
    const emailValidation = isValidEmail(policy.Rim.emailPolicyHolder);
    if (emailValidation.resultType === BusinessResultType.error) {
      result.resultType = BusinessResultType.error;
      result.messages.push(...emailValidation.messages);
    }
  }

  if (policy.Rim.sendEmailToPersonalExpert) {
    const emailValidation = isValidEmail(policy.Rim.emailPersonalExpert);
    if (emailValidation.resultType === BusinessResultType.error) {
      result.resultType = BusinessResultType.error;
      result.messages.push(...emailValidation.messages);
    }
  }

  if (policy.Rim.sendEmailToCounterExpert) {
    const emailValidation = isValidEmail(policy.Rim.emailCounterExpert);
    if (emailValidation.resultType === BusinessResultType.error) {
      result.resultType = BusinessResultType.error;
      result.messages.push(...emailValidation.messages);
    }
  }

  if (policy.Rim.sendEmailToOther) {
    const emailValidation = isValidEmail(policy.Rim.emailOther);
    if (emailValidation.resultType === BusinessResultType.error) {
      result.resultType = BusinessResultType.error;
      result.messages.push(...emailValidation.messages);
    }
  }

  return result;
}

export function validateRimLines(policy: BusinessPolicy): BusinessResult {
  const validationInProgress: BusinessValidation = {
    businessValidationType: BusinessValidationType.none,
    errorMessage: '',
    hasError: false,
  };
  const result: BusinessResult = {
    resultType: BusinessResultType.ok,
    messages: [],
    data: null,
    validations: [validationInProgress],
  };
  const nonGeneralItems = policy.Rim.rimLines.filter(x => x.rimSectionTC !== RimSectionTC.Gen && !x.deleted);
  if (!nonGeneralItems || nonGeneralItems.length === 0) {
    result.resultType = BusinessResultType.error;
    result.messages.push('validation.rim.oneLineRequired');
  }

  if (getRimLinesSum(policy.Rim.rimLines) <= 0) {
    result.resultType = BusinessResultType.error;
    result.messages.push('validation.rim.rimLineSumGreaterThen0');
  }

  if (policy.Rim.rimLines.some(x => !x.deleted && +x.amount === 0)) {
    result.resultType = BusinessResultType.error;
    result.messages.push('validation.rim.rimLinesHaveZeroAmount');
  }

  // Only for Axon
  // We need a validation for the OV With the english franchise
  // When it is an english franchise then, the user needs to select both excess amount and comp.opt.franch
  const casecode = policy.id;
  if (casecode > 10000000) {
    if (policy.loweredExcessAmountThreshold > 0)  { // This means English Franchise
      const compOpFranchise = policy.Rim.rimLines.filter(x => x.rimSectionTC === RimSectionTC.Gen && x.rimItemTC === RimItemTC.CompOptieFranchise);
      if (!compOpFranchise || compOpFranchise.length > 0) {
        // They have selected the comp.opt.franchise, we need to check if the excess amount has been selected
        const excessamount = policy.Rim.rimLines.filter(x => x.rimSectionTC === RimSectionTC.Gen && x.rimItemTC === RimItemTC.Vrijstelling);
        if (!excessamount || excessamount.length === 0) {
          result.resultType = BusinessResultType.error;
          result.messages.push('validation.rim.excessAmountShouldBeSelected');
        }
      }
    }
  }

  return result;
}

export function businessUpdateRimIBAN(policy: BusinessPolicy): BusinessResult {
  const validationInProgress: BusinessValidation = {
    businessValidationType: BusinessValidationType.ibanRim,
    errorMessage: '',
    hasError: false,
  };
  const result: BusinessResult = {
    resultType: BusinessResultType.ok,
    messages: [],
    data: null,
    validations: [validationInProgress],
  };
  result.data = policy;
  const ibanLength = getIBANLength(policy.Rim.iban);

  if (ibanLength === 0) {
    result.resultType = BusinessResultType.error;
    result.messages.push('validation.iban.notValid');
    const businessError = { businessValidationType: BusinessValidationType.ibanRim, errorMessage: 'validation.iban.notValid', hasError: true} as BusinessValidation;
    result.validations.push(businessError);
  } else {
    const ibanValidation = isValidIBAN(policy.Rim.iban);
    result.resultType = ibanValidation.resultType;
    result.messages = ibanValidation.messages;
    result.messages.forEach(item => {
      const error = { businessValidationType: BusinessValidationType.ibanRim, errorMessage: item, hasError: true} as BusinessValidation;
      result.validations.push(error);
    });
    policy.Rim.iban = policy.Rim.iban.slice(0, ibanLength);
  }

  return result;
}

export function businessUpdateRimBBAN(policy: BusinessPolicy): BusinessResult {
  const validationInProgress: BusinessValidation = {
    businessValidationType: BusinessValidationType.none,
    errorMessage: '',
    hasError: false,
  };
  const result: BusinessResult = {
    resultType: BusinessResultType.ok,
    messages: [],
    data: null,
    validations: [validationInProgress],
  };
  result.data = policy;
  const bbanLength = 12;

  if (policy.Rim.bban.length >= bbanLength) {
    const bbanValidation = isValidBBAN(policy.Rim.bban);
    result.resultType = bbanValidation.resultType;
    result.messages = bbanValidation.messages;
    policy.Rim.bban = policy.Rim.bban.slice(0, bbanLength);
  }

  return result;
}

export function isCocoon(conditions: string): boolean {
  return conditions === Constants.COCOON_CONDITIONS;
}

export function showFranchiseMissingWarning(rimLines: RimLine[]) {
  return (rimLines.filter(x => !x.deleted && (x.rimItemTC === RimItemTC.Vrijstelling || x.rimItemTC === RimItemTC.OV)).length < 2);
}
